import {
  Alert,
  Popover,
  PopoverButton,
  PopoverContent,
} from "@deere/fuel-react";
import NotificationImportantOutlinedIcon from "@mui/icons-material/NotificationImportantOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, Button, Divider, IconButton, Typography } from "@mui/material";
import Badge from "@mui/material/Badge";
import { useTheme } from "@mui/material/styles";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import AlertItem from "./AlertItem";
import { AppNotificationContext } from "../../../../../../../context/AppNotificationContext/AppNotificationContext";
import {
  filterAlertsToShowInHeader,
  getUnreadAlertCount,
  hasUnreadAlerts,
} from "../../../../../../../context/AppNotificationContext/reducer/selector";
import { ConfigContext } from "../../../../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../../../../context/DealerAccountContext";
import { RBACAccessContext } from "../../../../../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../../../../../context/UserContext/UserContext";
import { isAuthorizedForMultipleAccounts } from "../../../../../../../pages/preferences/utils";
import { mapAlertListItems } from "../utils";

const Alerts = () => {
  const { t, i18n } = useTranslation(["common", "alertsAndNotifications"]);
  const theme = useTheme();
  const { alerts, isRealTimeUpdatesDown, markAlertsAsRead } = useContext(
    AppNotificationContext
  );
  const {
    NUMBER_OF_ALERTS_TO_SHOW = 30,
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
  } = useContext(ConfigContext);
  const user = useContext(UserContext);
  const { selectedAccounts } = useContext(DealerAccountContext);
  const { isCorporateViewEnabled } = useContext(RBACAccessContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const isMultipleAccountViewActive =
    isCorporateViewEnabled &&
    isAuthorizedForMultipleAccounts(
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    ) &&
    selectedAccounts.length > 1;

  const hasUnReadAlert = hasUnreadAlerts(alerts);
  const unreadAlertCount = getUnreadAlertCount(alerts);
  const alertsToShowInHeader = filterAlertsToShowInHeader(alerts).slice(
    0,
    NUMBER_OF_ALERTS_TO_SHOW
  );
  const alertListItems = mapAlertListItems(
    alertsToShowInHeader,
    i18n.language,
    t,
    isMultipleAccountViewActive,
    navigate,
    markAlertsAsRead
  );

  const getBadgeColor = () => {
    if (!hasUnReadAlert) {
      return theme.tokens.FuelColorTextIcon;
    }
    return isRealTimeUpdatesDown
      ? theme.tokens.FuelColorAlertErrorIcon
      : theme.tokens.FuelColorAlertWarningIcon;
  };

  return (
    <Popover
      showClose={false}
      isOpen={isMenuOpen}
      onClose={() => setIsMenuOpen(false)}
    >
      <PopoverButton
        sx={{
          border: "none",
          boxShadow: "none",
        }}
        onClick={() => {
          setIsMenuOpen((prevState) => !prevState);
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: hasUnReadAlert
              ? theme.tokens.FuelSpacing4
              : theme.tokens.FuelSpacing1,
          }}
        >
          <Box
            style={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Badge
              badgeContent={unreadAlertCount}
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: getBadgeColor(),
                  color: theme.tokens.FuelColorWhite,
                },
              }}
            >
              <NotificationImportantOutlinedIcon
                sx={{
                  color: hasUnReadAlert
                    ? theme.tokens.FuelColorAlertWarningIcon
                    : theme.tokens.FuelColorTextIcon,
                }}
              />
            </Badge>
          </Box>
          <Typography
            sx={{
              fontSize: theme.tokens.FuelFontSizeSm,
              lineHeight: 1,
              padding: 0,
              margin: 0,
              display: "flex",
              alignItems: "center",
              fontWeight: theme.tokens.FuelFontWeightSemibold,
              color: theme.tokens.FuelColorTextTertiary,
            }}
          >
            {t("header.appNotification.alerts")}
          </Typography>
        </Box>
      </PopoverButton>
      <PopoverContent>
        <Box
          sx={{
            backgroundColor: theme.tokens.FuelColorWhite,
            borderRadius: 2,
            boxShadow: 1,
            width: "463px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              height: "63px",
              padding: `1.25rem ${theme.tokens.FuelSpacing4}`,
            }}
          >
            <Typography
              sx={{
                fontWeight: theme.tokens.FuelFontWeightSemibold,
                fontSize: theme.tokens.FuelFontSizeLg,
                lineHeight: "28px",
                letterSpacing: "0%",
                position: "relative",
              }}
            >
              {t("header.appNotification.alerts")}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                navigate(`/preferences`);
                setIsMenuOpen(false);
              }}
              aria-label="view preferences"
            >
              <TuneIcon />
            </IconButton>
          </Box>

          <Divider
            sx={{
              mx: "auto",
              borderBottomWidth: "1px",
              opacity: 1,
              position: "relative",
            }}
          />

          {isRealTimeUpdatesDown && (
            <Box
              sx={{
                paddingX: theme.tokens.FuelSpacing4,
                paddingTop: theme.tokens.FuelSpacing4,
              }}
            >
              <Alert variant="error">
                {t("header.alerts.realTimeUpdatesInactiveMessage")}
              </Alert>
            </Box>
          )}

          {alertListItems.length === 0 ? (
            <Box marginY={theme.tokens.FuelSpacing12}>
              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  color: theme.tokens.FuelColorTextDisabled,
                }}
              >
                {t("header.alerts.noAlerts")}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                position: "relative",
                overflowY: "auto",
                maxHeight: 392,
                padding: theme.tokens.FuelSpacing4,
              }}
            >
              {alertListItems.map((alert) => (
                <AlertItem
                  key={alert.id}
                  {...alert}
                  onButtonClick={
                    "onButtonClick" in alert
                      ? () => {
                          alert.onButtonClick();
                          setIsMenuOpen(false);
                        }
                      : undefined
                  }
                />
              ))}
            </Box>
          )}

          <Divider />

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              px: "119px",
              py: "16px",
            }}
          >
            <Button
              onClick={() => {
                navigate(`/alerts-notifications-center`);
                setIsMenuOpen(false);
              }}
              sx={{
                border: "none",
                boxShadow: "none",
              }}
            >
              <Typography
                sx={{
                  fontWeight: theme.tokens.FuelFontWeightSemibold,
                  fontSize: theme.tokens.FuelFontSizeSm,
                  lineHeight: "1.25rem",
                  letterSpacing: "0%",
                  color: theme.tokens.FuelColorJdGreen,
                  alignItems: "center",
                }}
              >
                {t("header.appNotification.viewAllAlertsAndNotifications")}
              </Typography>
            </Button>
          </Box>
        </Box>
      </PopoverContent>
    </Popover>
  );
};

export default Alerts;
